import Criterion from '.';
import {
  AccessControlInterfaceType,
  AccessControlOptionsType
} from '../accessControl/types';
import {
  EntitlementsInterfaceType,
  OfferingAndEntitlementType,
  OfferingAndEntitlementOptionsType
} from '../entitlements/types';
import { GrantsInterfaceType } from '@clientos/types';
import { GrantsHistoryInterfaceType } from '@clientos/types';
import { ILocalizationService } from '../../../services/localizationService';
import { ISessionService } from '../../../services/session';
import { AuthContextEnum } from '../../../services/authTokenService';
import { IFeatureFlagService } from '../../../services/featureFlagService';
import { IUserService } from '../../../services/userService';
import { GrantOptionsType, GrantType } from '../../../services/grants/types';
import {
  GrantHistoryOptionsType,
  GrantHistoryType
} from '../../../services/grants/grantsHistory/types';
import ITenantHandlerService from '../../../services/tenantHandler/ITenantHandlerService';
import valueFunctions from './valueFunctions';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { CriterionInputType } from '../../../infra/startup/types';
import { ServiceLocalizationLanguageType } from '../../../services/localizationService/types';
import { IEventService } from '../../../services/eventService';
import { TenantDataType } from '../../../services/tenantHandler/strategy/types';
import { isNative as isNativeFromJWeb } from '../../../services/JWeb';
import { MathOpType } from './types';

type CreateCriterionSingletonPropsType = {
  interfaces: {
    entitlements: EntitlementsInterfaceType;
    accessControl: AccessControlInterfaceType;
    grants: GrantsInterfaceType;
    grantsHistory: GrantsHistoryInterfaceType;
  };
  services: {
    eventService: IEventService;
    sessionService: ISessionService;
    tenantHandler: ITenantHandlerService;
    featureFlagService: IFeatureFlagService;
    userService: IUserService;
    localization: ILocalizationService;
  };
  criterions: CriterionInputType<any>[];
};

let criterionSingleton: ReturnType<typeof _createCriterion>;

function _createCriterion(options: CreateCriterionSingletonPropsType) {
  const { accessControl, entitlements, grants, grantsHistory } =
    options.interfaces;
  const {
    eventService,
    sessionService,
    tenantHandler,
    featureFlagService,
    userService,
    localization
  } = options?.services || {};

  const criterionInterface = new Criterion({
    valueFunctions: {
      // @ts-ignore
      entitlement: async (
        value: OfferingAndEntitlementType,
        options?: OfferingAndEntitlementOptionsType
      ) => !!(await entitlements.checkEntitlements([value], options)),
      // @ts-ignore
      offering: async (
        value: OfferingAndEntitlementType,
        options?: OfferingAndEntitlementOptionsType
      ) => !!(await entitlements.checkOfferings([value], options)),
      // @ts-ignore
      scope: async (value: string, options?: AccessControlOptionsType) => {
        let hasScopes = false;
        const loggedIn = !!sessionService.isLoggedIn();
        if (loggedIn) {
          hasScopes = !!(await accessControl.check([value], options));
        }
        return hasScopes;
      },
      // @ts-ignore
      action: async (value: string, options?: AccessControlOptionsType) => {
        let isAllowed = false;
        const loggedIn = !!sessionService.isLoggedIn();
        if (loggedIn) {
          isAllowed = !!(await accessControl.check([value], options));
        }
        return isAllowed;
      },
      isLoggedIn: async (value: boolean) => {
        const isLoggedIn = !!sessionService?.isLoggedIn?.();

        return value ? isLoggedIn : !isLoggedIn;
      },
      authContext: async (value: { context: AuthContextEnum }) => {
        const authContext = value?.context;

        if (!authContext) return false;

        const currentAuthContext = tenantHandler.getCurrentContext();

        return authContext === currentAuthContext;
      },
      featureFlag: async (value: {
        clientKey: string;
        featureFlag: string;
        value: unknown;
        defaultValue: unknown;
      }) => {
        const client = await featureFlagService.getClient(value?.clientKey);
        const featureFlag = await client.getFeatureFlag({
          key: value?.featureFlag,
          defaultValue: value?.defaultValue
        });
        return featureFlag === value?.value;
      },
      userDetails: async (value: { countries: string[] }) => {
        const country = await userService.getCountry();
        return value?.countries
          ?.map((c) => c.toLocaleLowerCase())
          ?.includes(country);
      },
      locale: async (value: { languages: ServiceLocalizationLanguageType }) => {
        return !!(await localization.checkAllowedLanguages(value?.languages));
      },
      // @ts-ignore
      grant: async (value: GrantType, options?: GrantOptionsType) => {
        return !!(await grants.checkGrants([value], options));
      },

      grantHistory: async (
        value: GrantHistoryType,
        options?: GrantHistoryOptionsType
      ) => {
        return !!(await grantsHistory.checkGrantsHistory([value], options));
      },
      // TODO: this should be fixed ASAP
      // @ts-ignore
      pendingGrant: async (value: GrantType, options?: GrantOptionsType) => {
        // @ts-ignore
        return !!(await grants.checkPendingGrants([value], options));
      },
      tenant: async (value: {
        type?: string[];
        roleCategory?: string;
        roleName?: string;
        countries?: string[];
        regionId?: string;
      }) => {
        try {
          const isNative = await isNativeFromJWeb();
          const tenant = tenantHandler.getTenantByContext(
            AuthContextEnum.tenant
          );
          // @TODO remove this workaround when checkCriterion occurs only after portals have a chosen tenant.
          if (!isNative && !tenant) {
            return true;
          }

          let checkRoleCategory = true;
          let checkRoleName = true;
          let checkTenantType = true;
          let checkTenantCountries = true;
          let checkTenantRegionId = true;

          if (value?.roleCategory) {
            if (!tenant?.data?.roleCategory) {
              if (!tenant.data) {
                tenant.data = {} as TenantDataType;
              }
              // @TODO remove this workaround after undestand why in the first access the user does not have roleCategory

              tenant.data.roleCategory = await updateTenantRoleCategory(
                tenantHandler,
                tenant
              );
            }

            checkRoleCategory = !!(
              value?.roleCategory?.toLowerCase() ===
              tenant?.data?.roleCategory?.toLowerCase()
            );
          }
          if (value?.roleName) {
            checkRoleName = !!(
              value?.roleName?.toLowerCase() ===
              tenant?.data?.roleName?.toLowerCase()
            );
          }

          if (value?.type) {
            checkTenantType = !!value?.type
              ?.map((t) => t.toLowerCase())
              ?.includes(tenant?.data?.type?.toLowerCase());
          }

          if (value?.countries) {
            const { countries } =
              await tenantHandler.getCurrentTenantAdditionalData();
            checkTenantCountries = countries.some((country) =>
              value.countries?.includes(country)
            );
          }

          if (value?.regionId) {
            const { regionId } =
              await tenantHandler.getCurrentTenantAdditionalData();
            checkTenantRegionId = value.regionId === regionId;
          }

          return (
            checkRoleCategory &&
            checkRoleName &&
            checkTenantType &&
            checkTenantCountries &&
            checkTenantRegionId
          );
        } catch (error) {
          internalLogger?.error(
            'The tenant criteria is poorly formatted ',
            error
          );
          return false;
        }
      },

      tenantList: async (value: {
        count?: { inputValue: string; mathOp: MathOpType };
      }) => {
        return evaluateTenantListCount(tenantHandler, value?.count);
      },
      // @ts-ignore
      programInfo: async (
        value: { programLevel: string; state?: string },
        options?: OfferingAndEntitlementOptionsType
      ) => {
        const programInfo = await entitlements.getPlanInfo(options);

        if (value?.state && value?.programLevel) {
          return (
            programInfo?.programLevel == value.programLevel &&
            programInfo?.state == value.state
          );
        } else if (value?.state) {
          return programInfo?.state == value?.state;
        } else if (value?.programLevel) {
          return programInfo?.programLevel == value?.programLevel;
        }

        return false;
      },

      ...valueFunctions
    },
    criterionList: options?.criterions as any,
    eventService: eventService,
    eventList: [
      eventService.eventNames.shellAccessControlChangedEventName,
      eventService.eventNames.shellEntitlementChangedEventName,
      eventService.eventNames.shellUserLoggedInEventName,
      eventService.eventNames.shellUserLoggedOutEventName,
      eventService.eventNames.shellFeatureFlagChangedEventName,
      eventService.eventNames.shellGrantsChangedEventName,
      eventService.eventNames.shellPendingGrantsChangedEventName,
      eventService.eventNames.shellGrantsHistoryChangedEventName,
      eventService.eventNames.shellTenantChangedEventName,
      eventService.eventNames.shellUrlChanged,
      eventService.eventNames.shellCriterionForceCheckUpdate
    ],
    eventName: eventService.eventNames.shellCriterionChangedEventName
  });

  criterionInterface.addValueFunctions({
    criterion: async (value: string) => {
      return criterionInterface.checkByCriterionKey(value);
    }
  });

  return criterionInterface;
}

async function updateTenantRoleCategory(tenantHandler, tenant) {
  try {
    const tenantList = await tenantHandler.getTenantList({
      authContext: AuthContextEnum.tenant
    });

    const tenantByList = tenantList?.find((t) => tenant?.id === t?.id);

    return tenantByList?.roleCategory || '';
  } catch (error) {
    internalLogger?.error('Error updating tenant role category:', error);
    return '';
  }
}

const tenantListCountOperations: Record<
  MathOpType,
  (current: number, expected: number) => boolean
> = {
  eq: (c, e) => c === e,
  ne: (c, e) => c !== e,
  gt: (c, e) => c > e,
  gte: (c, e) => c >= e,
  lt: (c, e) => c < e,
  lte: (c, e) => c <= e
};

function parseCountInput(raw: string): number | null {
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

async function evaluateTenantListCount(
  tenantHandler: ITenantHandlerService,
  countConfig?: { inputValue: string; mathOp: MathOpType }
): Promise<boolean> {
  if (!countConfig) {
    internalLogger?.error('tenantList criterion: missing countConfig');
    return false;
  }

  const parsed = parseCountInput(countConfig.inputValue);

  if (parsed === null) {
    internalLogger?.error(
      `tenantList criterion: inputValue="${countConfig.inputValue}" is not a valid number`
    );
    return false;
  }

  try {
    const tenantList = await tenantHandler.getTenantList({
      authContext: AuthContextEnum.tenant
    });
    const size = Array.isArray(tenantList) ? tenantList.length : 0;
    return tenantListCountOperations[countConfig.mathOp](size, parsed);
  } catch (error) {
    internalLogger?.error('Error evaluating tenantList criterion:', error);
    return false;
  }
}

export function getCriterionSingleton() {
  return criterionSingleton;
}

export function clearCriterionSingleton(): void {
  criterionSingleton = undefined;
}

export default function initializeCriterionSingleton(
  options: CreateCriterionSingletonPropsType
) {
  if (criterionSingleton) return criterionSingleton;

  criterionSingleton = _createCriterion(options);

  return criterionSingleton;
}
